import { IconButton, Tooltip, useTheme } from "@material-ui/core";
import { Check, ChevronRight, FileCopy } from "@material-ui/icons";
import React, { useState, useMemo, useCallback, memo } from "react";

// ---------------------------------------------------------------------------
// 1. Robust Parsing & Normalization
// ---------------------------------------------------------------------------
const tryParse = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  const looksLikeJson =
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"));

  if (!looksLikeJson) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const deepNormalize = (value: unknown, depth = 0): unknown => {
  if (depth > 30) return value; // Safety net for pathological nested structures
  const parsed = tryParse(value);

  if (Array.isArray(parsed)) {
    return parsed.map((item) => deepNormalize(item, depth + 1));
  }

  if (parsed && typeof parsed === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(parsed)) {
      out[k] = deepNormalize(v, depth + 1);
    }
    return out;
  }

  return parsed;
};

// ---------------------------------------------------------------------------
// 2. Theme-Aware Color Palette (One Dark / GitHub inspired)
// ---------------------------------------------------------------------------
const getValueColor = (value: unknown, isDark: boolean): string => {
  if (value === null) return isDark ? "#5c6370" : "#858585";
  if (typeof value === "boolean") return isDark ? "#c678dd" : "#a626a4";
  if (typeof value === "number") return isDark ? "#d19a66" : "#986801";
  if (typeof value === "string") return isDark ? "#98c379" : "#50a14f";
  return isDark ? "#abb2bf" : "#383a42";
};

const getKeyColor = (isDark: boolean): string => (isDark ? "#61afef" : "#4078f2");
const getBracketColor = (isDark: boolean): string => (isDark ? "#abb2bf" : "#999999");
const getBgColor = (isDark: boolean): string => (isDark ? "#282c34" : "#f6f8fa");
const getBorderColor = (isDark: boolean): string => (isDark ? "#3e4451" : "#e1e4e8");
const getGuideColor = (isDark: boolean): string => (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)");

const FONT_STACK = "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', 'Monaco', monospace";

// ---------------------------------------------------------------------------
// 3. Recursive JSON Node (Memoized for Performance)
// ---------------------------------------------------------------------------
interface JsonNodeProps {
  data: unknown;
  keyName?: string;
  isLast?: boolean;
  isDark: boolean;
}

const JsonNode = memo(function JsonNode({ data, keyName, isLast = true, isDark }: JsonNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const isArray = Array.isArray(data);
  const isObject = data !== null && typeof data === "object" && !isArray;

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  const lineStyle: React.CSSProperties = {
    fontFamily: FONT_STACK,
    fontSize: 13,
    lineHeight: 1.6,
    backgroundColor: isHovered ? (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)") : "transparent",
    borderRadius: 4,
    transition: "background-color 0.15s ease",
    paddingLeft: 4,
    paddingRight: 4,
    marginLeft: -4,
    marginRight: -4,
  };

  // Primitive rendering
  if (!isObject && !isArray) {
    return (
      <div style={lineStyle} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <span>
          {keyName !== undefined && (
            <>
              <span style={{ color: getKeyColor(isDark), fontWeight: 500 }}>"{keyName}"</span>
              <span style={{ color: getBracketColor(isDark) }}>: </span>
            </>
          )}
          <span style={{ color: getValueColor(data, isDark), fontWeight: 500 }}>
            {typeof data === "string" ? `"${data}"` : String(data)}
          </span>
          {!isLast && <span style={{ color: getBracketColor(isDark) }}>,</span>}
        </span>
      </div>
    );
  }

  const entries = Object.entries(data as object);
  const isEmpty = entries.length === 0;
  const openBracket = isArray ? "[" : "{";
  const closeBracket = isArray ? "]" : "}";

  if (isEmpty) {
    return (
      <div style={lineStyle} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <span>
          {keyName !== undefined && (
            <>
              <span style={{ color: getKeyColor(isDark), fontWeight: 500 }}>"{keyName}"</span>
              <span style={{ color: getBracketColor(isDark) }}>: </span>
            </>
          )}
          <span style={{ color: getBracketColor(isDark), fontWeight: 600 }}>{openBracket}{closeBracket}</span>
          {!isLast && <span style={{ color: getBracketColor(isDark) }}>,</span>}
        </span>
      </div>
    );
  }

  return (
    <div style={lineStyle} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", userSelect: "none", outline: "none" }}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
      >
        <span
          style={{
            color: getBracketColor(isDark),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          <ChevronRight style={{ fontSize: 16 }} />
        </span>

        {keyName !== undefined && (
          <>
            <span style={{ color: getKeyColor(isDark), fontWeight: 500 }}>"{keyName}"</span>
            <span style={{ color: getBracketColor(isDark) }}>: </span>
          </>
        )}

        <span style={{ color: getBracketColor(isDark), fontWeight: 600 }}>{openBracket}</span>

        {!expanded && (
          <>
            <span style={{ color: isDark ? "#5c6370" : "#999", fontSize: 12, margin: "0 6px", fontStyle: "italic" }}>
              {entries.length} {entries.length === 1 ? "item" : "items"}
            </span>
            <span style={{ color: getBracketColor(isDark), fontWeight: 600 }}>{closeBracket}</span>
            {!isLast && <span style={{ color: getBracketColor(isDark) }}>,</span>}
          </>
        )}
      </div>

      {expanded && (
        <div
          style={{
            paddingLeft: 20,
            borderLeft: `1px solid ${getGuideColor(isDark)}`,
            marginLeft: 5,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          {entries.map(([k, v], i) => (
            <JsonNode
              key={k}
              data={v}
              keyName={isArray ? undefined : k}
              isLast={i === entries.length - 1}
              isDark={isDark}
            />
          ))}
          <div>
            <span style={{ color: getBracketColor(isDark), fontWeight: 600 }}>{closeBracket}</span>
            {!isLast && <span style={{ color: getBracketColor(isDark) }}>,</span>}
          </div>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// 4. Main Viewer Wrapper
// ---------------------------------------------------------------------------
interface JsonViewerProps {
  data: unknown;
  /** Set false to render the tree at its full natural height with no internal
   * scrollbar — use this when a parent (e.g. a dialog that sizes itself to
   * the content) needs to measure the real content height. Default true. */
  scrollable?: boolean;
  /** Max height of the internal scroll box when scrollable is true. Default 500. */
  maxHeight?: number | string;
}

export default function JsonViewer({ data, scrollable = true, maxHeight = "calc(100vh - 344px)" }: JsonViewerProps) {
  const [copied, setCopied] = useState(false);

  // Gracefully supports both MUI v5 (mode) and v4 (type)
  const theme: any = useTheme();
  const isDark = theme.palette?.mode === "dark" || theme.palette?.type === "dark";

  const normalized = useMemo(() => deepNormalize(data), [data]);

  const copy = useCallback(() => {
    navigator.clipboard
      .writeText(JSON.stringify(normalized, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy JSON:", err));
  }, [normalized]);

  if (data === undefined || data === null) {
    return (
      <div
        style={{
          fontFamily: FONT_STACK,
          fontSize: 13,
          color: isDark ? "#5c6370" : "#858585",
          padding: 24,
          textAlign: "center",
          backgroundColor: getBgColor(isDark),
          border: `1px solid ${getBorderColor(isDark)}`,
          borderRadius: 8,
        }}
      >
        No data to display
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        background: getBgColor(isDark),
        border: `1px solid ${getBorderColor(isDark)}`,
        borderRadius: 8,
        padding: "16px 48px 16px 16px",
        maxHeight: scrollable ? maxHeight : "none",
        overflow: scrollable ? "auto" : "visible",
        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.06)",
        transition: "all 0.2s ease",
      }}
    >
      <Tooltip title={copied ? "Copied to clipboard!" : "Copy JSON"} placement="top">
        <IconButton
          onClick={copy}
          size="small"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
            padding: 6,
            width: 32,
            height: 32,
            transition: "all 0.2s ease",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
          }}
        >
          {copied ? (
            <Check style={{ fontSize: 18, color: "#98c379" }} />
          ) : (
            <FileCopy style={{ fontSize: 18, color: isDark ? "#abb2bf" : "#6a737d" }} />
          )}
        </IconButton>
      </Tooltip>

      <JsonNode data={normalized} isDark={isDark} />
    </div>
  );
}
